// App-Rahmen (Pflichtenheft 12.1): Navigation links (Desktop) bzw. unten (Mobil ≤ 768 px), Mandantenwahl,
// Sprache, Hell/Dunkel, Benutzermenü, Hinweis bei aktivem Support-Zugriff.
import { useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useApprovalState } from '../api/approvals';
import { api } from '../api/client';
import { can, qk, useMe, useResetTenantData, useTenants } from '../api/queries';
import type { Me } from '../api/types';
import { localeOf, setLanguage } from '../i18n';
import { useTheme } from '../theme';
import { Brand } from './Brand';
import { EmergencyBanner } from './EmergencyBanner';
import { Icon } from './Icon';
import { useToast } from './toast';
import { Dialog, Field, formatDateTime } from './ui';
import { hasFeature, type FeatureKey } from '../api/features';

interface NavItem { to: string; icon: string; label: string; perm?: string; tenant?: boolean; count?: number; feature?: FeatureKey }

export function Shell({ children }: { children: ReactNode }) {
  const { data: me } = useMe();
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const theme = useTheme();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const { data: approvals } = useApprovalState();
  if (!me) return null;
  // Studio/Designer nutzt die ganze Fläche
  if (loc.pathname.startsWith('/studio/') || /\/items\/[^/]+\/design$/.test(loc.pathname)) return <>{children}</>;

  const items: NavItem[] = [
    { to: '/', icon: 'home', label: t('nav.dashboard'), perm: 'monitoring.read', tenant: true },
    { to: '/screens', icon: 'screen', label: t('nav.screens'), perm: 'devices.read', tenant: true },
    { to: '/playlists', icon: 'list', label: t('nav.playlists'), perm: 'playlists.read', tenant: true },
    { to: '/media', icon: 'image', label: t('nav.media'), perm: 'media.read', tenant: true },
    { to: '/templates', icon: 'layout', label: t('nav.templates'), perm: 'layouts.read', tenant: true },
    { to: '/schedules', icon: 'calendar', label: t('nav.schedules'), perm: 'playlists.read', tenant: true },
    { to: '/datasources', icon: 'database', label: t('nav.datasources'), perm: 'datasources.read', tenant: true, feature: 'datasources' },
    { to: '/locations', icon: 'pin', label: t('nav.locations'), perm: 'locations.read', tenant: true },
    { to: '/monitoring', icon: 'alert', label: t('monitoring.nav'), perm: 'monitoring.read', tenant: true, feature: 'monitoring' },
    { to: '/reports', icon: 'chart', label: t('reports.nav'), perm: 'reports.read', tenant: true, feature: 'reports' },
    { to: '/users', icon: 'users', label: t('nav.users'), perm: 'users.read', tenant: true },
    { to: '/emergency', icon: 'megaphone', label: t('emergency.nav'), perm: 'devices.read', tenant: true, feature: 'emergency' },
  ];
  // Freigaben: sichtbar, wenn eingeschaltet, für Administratoren (zum Einschalten) oder bei eigenen offenen Anfragen
  if (approvals && (approvals.enabled || approvals.canApprove || approvals.mine > 0)) {
    items.splice(6, 0, { to: '/approvals', icon: 'check', label: t('approval.nav'), perm: 'playlists.read', tenant: true, count: approvals.toDecide, feature: 'approval' });
  }
  // Gebetszeiten (Moscheen/Vereine): nach „Vorlagen"
  items.splice(items.findIndex((i) => i.to === '/templates') + 1, 0, { to: '/prayer-times', icon: 'moon', label: t('nav.prayer'), perm: 'layouts.read', tenant: true, feature: 'prayer' });
  // Funktionen & Vorlagen: abgeschaltete Module erscheinen nicht im Menü
  const visible = items.filter((i) => (!i.tenant || me.activeTenantId) && (!i.perm || can(me, i.perm)) && (!i.feature || hasFeature(me, i.feature)));
  const platform = can(me, 'tenants.manage');
  const initials = (me.user.displayName || me.user.email).split(/[\s@.]/).filter(Boolean).slice(0, 2).map((s) => s[0]!.toUpperCase()).join('');

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      qc.clear();
      qc.setQueryData(qk.me, null);
      navigate('/login');
    }
  };
  const endSupport = async () => {
    try {
      await api.del('/platform/support-sessions/current');
      await qc.invalidateQueries({ queryKey: qk.me });
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
      navigate('/platform/tenants');
    } catch (e) {
      toast.error(e);
    }
  };

  const nav = (
    <nav className="nav" aria-label={t('nav.main')}>
      {visible.map((i) => (
        <NavLink key={i.to} to={i.to} end={i.to === '/'} className={({ isActive }) => (isActive ? 'active' : '')} onClick={() => setOpen(false)}>
          <Icon name={i.icon} />{i.label}
          {i.count ? <span className="nav-count" aria-label={`(${i.count})`}>{i.count}</span> : null}
        </NavLink>
      ))}
      {platform && <>
        <div className="lbl">{t('nav.platform')}</div>
        <NavLink to="/platform/tenants" onClick={() => setOpen(false)} className={({ isActive }) => (isActive ? 'active' : '')}>
          <Icon name="building" />{t('nav.tenants')}
        </NavLink>
        {can(me, 'system.settings') && <NavLink to="/platform/settings" onClick={() => setOpen(false)} className={({ isActive }) => (isActive ? 'active' : '')}>
          <Icon name="shield" />{t('monitoring.smtp.nav')}
        </NavLink>}
        {can(me, 'platform.admins.manage') && <NavLink to="/platform/admins" onClick={() => setOpen(false)} className={({ isActive }) => (isActive ? 'active' : '')}>
          <Icon name="key" />{t('platformAdmins.nav')}
        </NavLink>}
      </>}
    </nav>
  );

  return (
    <div className="shell">
      <header className="topbar">
        <button className="btn ghost icon" style={{ color: '#fff' }} aria-label={t('nav.menu')} onClick={() => setOpen(true)}><Icon name="menu" /></button>
        <Brand dark />
        <span className="grow" />
        <NavLink to="/account" className="btn ghost icon" style={{ color: '#fff' }} aria-label={t('account.menu')} title={t('account.menu')}><Icon name="users" /></NavLink>
        <button className="btn ghost icon" style={{ color: '#fff' }} aria-label={t('shell.theme')} onClick={theme.toggle}>
          <Icon name={theme.resolved === 'dark' ? 'sun' : 'moon'} />
        </button>
      </header>
      {open && <div className="scrim" onClick={() => setOpen(false)} />}
      <aside className={`side${open ? ' open' : ''}`}>
        <Brand />
        <TenantSwitch me={me} />
        {nav}
        <div className="col" style={{ marginTop: 'auto', gap: 10 }}>
          <div className="row" style={{ gap: 6, padding: '0 4px' }}>
            <div className="seg" role="group" aria-label={t('shell.language')}>
              {(['de', 'en'] as const).map((l) => (
                <button key={l} className={i18n.language === l ? 'on' : ''} aria-pressed={i18n.language === l} onClick={() => setLanguage(l)}>{l.toUpperCase()}</button>
              ))}
            </div>
            <button className="btn ghost icon" title={t('shell.theme')} aria-label={t('shell.theme')} onClick={theme.toggle}>
              <Icon name={theme.resolved === 'dark' ? 'sun' : 'moon'} />
            </button>
          </div>
          <div className="me" style={{ marginTop: 0 }}>
            <div className="avatar" aria-hidden>{initials}</div>
            {/* Benutzermenü: Name öffnet „Mein Konto“ (Passwort, Zwei-Faktor-Anmeldung, Sitzungen) */}
            <NavLink to="/account" className="who grow" title={t('account.menu')} onClick={() => setOpen(false)}>
              <b>{me.user.displayName || me.user.email}</b>
              <span className="muted">{me.user.isPlatformAdmin ? t('roles.PlatformAdmin') : me.activeRole ? t(`roles.${me.activeRole}`) : ''}</span>
            </NavLink>
            <NavLink to="/account" className="btn ghost icon" title={t('account.menu')} aria-label={t('account.menu')} onClick={() => setOpen(false)}><Icon name="key" /></NavLink>
            <button className="btn ghost icon" title={t('shell.logout')} aria-label={t('shell.logout')} onClick={logout}><Icon name="logout" /></button>
          </div>
        </div>
      </aside>
      <main className="content">
        {me.supportSession && (
          <div className="support-banner" role="status">
            <Icon name="shield" />
            <span className="grow">{t('shell.supportActive', { until: formatDateTime(me.supportSession.until, localeOf(i18n.language)), reason: me.supportSession.reason })}</span>
            <button className="btn sm" onClick={endSupport}>{t('shell.endSupport')}</button>
          </div>
        )}
        <EmergencyBanner />
        {children}
      </main>
      <nav className="bottom-nav" aria-label={t('nav.mobile')}>
        {visible.slice(0, 4).map((i) => (
          <NavLink key={i.to} to={i.to} end={i.to === '/'} className={({ isActive }) => (isActive ? 'active' : '')}><Icon name={i.icon} />{i.label}</NavLink>
        ))}
        <button onClick={() => setOpen(true)}><Icon name="more" />{t('nav.more')}</button>
      </nav>
    </div>
  );
}

/** Mandantenwahl: nur bei mehreren Mitgliedschaften; Plattformadmins wählen per Support-Zugriff. */
function TenantSwitch({ me }: { me: Me }) {
  const { t } = useTranslation();
  const tenants = useTenants();
  const reset = useResetTenantData();
  const toast = useToast();
  const navigate = useNavigate();
  const [support, setSupport] = useState<{ id: string; name: string } | null>(null);

  const options = new Map<string, string>();
  for (const m of me.memberships) options.set(m.tenantId, m.tenantName);
  if (me.user.isPlatformAdmin) for (const x of tenants.data ?? []) if (!options.has(x.id)) options.set(x.id, x.name);
  const activeName = me.activeTenantId ? options.get(me.activeTenantId) ?? '' : '';
  if (options.size <= 1 && !me.user.isPlatformAdmin) {
    return activeName ? <div className="muted small" style={{ padding: '0 10px' }}>{activeName}</div> : null;
  }

  const pick = async (id: string) => {
    if (!id || id === me.activeTenantId) return;
    if (me.memberships.some((m) => m.tenantId === id)) {
      try {
        reset(await api.post<Me>('/auth/switch-tenant', { tenantId: id }));
        navigate('/');
      } catch (e) {
        toast.error(e);
      }
    } else setSupport({ id, name: options.get(id) ?? '' });
  };

  return (
    <>
      <label className="field" style={{ padding: '0 4px' }}>
        <span>{t('shell.tenant')}</span>
        <select value={me.activeTenantId ?? ''} onChange={(e) => void pick(e.target.value)} aria-label={t('shell.tenant')}>
          <option value="">{t('shell.chooseTenant')}</option>
          {[...options].sort((a, b) => a[1].localeCompare(b[1])).map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
      </label>
      {support && <SupportDialog tenant={support} onClose={() => setSupport(null)} />}
    </>
  );
}

/** Support-Zugriff starten (Fussnote ² in 2.2): Grund und Dauer, protokolliert. */
export function SupportDialog({ tenant, onClose }: { tenant: { id: string; name: string }; onClose: () => void }) {
  const { t } = useTranslation();
  const reset = useResetTenantData();
  const toast = useToast();
  const navigate = useNavigate();
  const [reason, setReason] = useState('');
  const [minutes, setMinutes] = useState(60);
  const start = async () => {
    try {
      reset(await api.post<Me>('/platform/support-sessions', { tenantId: tenant.id, reason, minutes }));
      onClose();
      navigate('/');
    } catch (e) {
      toast.error(e);
    }
  };
  return (
    <Dialog title={t('platform.supportTitle', { name: tenant.name })} onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>{t('common.cancel')}</button>
      <button className="btn primary" disabled={reason.trim().length < 3} onClick={start}>{t('platform.startSupport')}</button>
    </>}>
      <div className="hint">{t('platform.supportHint')}</div>
      <Field label={t('platform.reason')}><input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('platform.reasonPh')} /></Field>
      <Field label={t('platform.minutes')}>
        <select value={minutes} onChange={(e) => setMinutes(Number(e.target.value))}>
          {[15, 30, 60, 120, 240].map((m) => <option key={m} value={m}>{m} min</option>)}
        </select>
      </Field>
    </Dialog>
  );
}
